import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { useAskRedoEnabled } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-enabled";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import { lazy, memo, ReactNode, Suspense } from "react";

/**
 * Wraps the merchant app shell with the Ask Redo slide-out column.
 *
 * Two responsibilities:
 *
 *   1. **Flag gate.** When the feature is off, render children
 *      verbatim — no wrapper div, no CSS grid, no `overflow:auto`
 *      sandwich. The dormant code path adds zero DOM cost.
 *
 *   2. **Bundle gate (panel-only).** When the feature is on, the
 *      heavy bit is the slide-out content (it transitively imports
 *      the AI SDK, transport, and every chat component). We
 *      `React.lazy()` that — but ONLY the panel slot, not the grid
 *      around the merchant content. Wrapping `children` inside a
 *      Suspense boundary would mean every route's state resets when
 *      the chunk lands, because React treats fallback children and
 *      resolved children as separate subtrees. Wrapping just the
 *      panel keeps `children` mounted at the same identity through
 *      the load.
 *
 * The grid + resize / notification-offset / focus-on-open behaviour
 * comes from the shared `AiSlidePanelLayout` primitive (also used by
 * the legacy `GlobalAI` and onboarding's `GlobalAskRedo`). We drive
 * its `isOpen` from our own context so the new panel's state is
 * isolated from the legacy one's.
 */
const LazyAskRedoSlideOut = lazy(() =>
  import("../slide-out/ask-redo-slide-out").then((m) => ({
    default: m.AskRedoSlideOut,
  })),
);

export const AskRedoLayout = memo(function AskRedoLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { enabled } = useAskRedoEnabled();
  const { isOpen } = useAskRedoSlidePanel();

  if (!enabled) {
    return <>{children}</>;
  }

  // `ClientUploader` provides `UploaderContext` so the prompt
  // input can offer image/file uploads. The legacy chat mounts its
  // own ClientUploader inside `GlobalAI`; we mount one here so the
  // Ask Redo slide-out + full page have the uploader available
  // even when the legacy panel is collapsed. Nested providers are
  // fine — the closest one wins for any given consumer.
  return (
    <ClientUploader>
      <AiSlidePanelLayout
        isOpen={isOpen}
        panel={
          // Suspense renders nothing while the chunk loads — the
          // panel's column collapses to 0 width when `isOpen` is
          // false anyway, so there's nothing to show. When the
          // merchant opens the panel for the first time, the
          // standard Chat empty state fills in immediately.
          <Suspense fallback={null}>
            <LazyAskRedoSlideOut />
          </Suspense>
        }
      >
        {children}
      </AiSlidePanelLayout>
    </ClientUploader>
  );
});
